import React, { useState } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import {
    ColorSwatch,
    DrawerBody,
    DrawerHeading,
    DrawerFooter,
    UncontrolledGenericDrawer
} from '@/components/common';
import { TextField } from '@/components/rhf-mui';
import { useDrawers, useToastNotification, useUrlParams } from '@/hooks';
import {
    createTag,
    updateTag,
    getPaginatedTags
} from '@/modules/dataWrangler/tags';
import { TagsIconBlue } from '@/resources/icons';
import { closeDrawer, updateDrawer } from '@/state/actions';
import { TAG_BASE_PARAM } from '@/constants';
import { useForm, FormProvider } from 'react-hook-form';
import useStyles from './styles';

const CreateTagsDrawer = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { getDrawer } = useDrawers();
    const { triggerToast } = useToastNotification();
    const [addTagLoading, setAddTagLoading] = useState(false);
    const location = useLocation();
    const { getUrlParams } = useUrlParams();
    const currentUrlParams = getUrlParams({ base: TAG_BASE_PARAM });

    const savedTags =
        useSelector((state) => state.user?.activeBusiness?.tags) || [];

    const createTagsDrawer = getDrawer('createTags');

    const tag = createTagsDrawer?.tag;
    const edit = createTagsDrawer?.edit;
    const type = createTagsDrawer?.type;
    const item = createTagsDrawer?.item;
    const title = createTagsDrawer?.title;
    const makeDocument = createTagsDrawer?.makeDocumentPage;
    const currentTags = createTagsDrawer?.currentTags;
    const currentValues = createTagsDrawer?.currentValues;
    const openedFromDrawer = createTagsDrawer?.openedFromDrawer;
    const drawerOpen = Boolean(createTagsDrawer);

    const methods = useForm({
        mode: 'onSubmit',
        values: {
            tagName: tag ? tag.name : '',
            selectedColor: tag
                ? {
                      backgroundColor: tag.backgroundColor,
                      textColor: tag.color
                  }
                : {
                      backgroundColor: '#94A3B8',
                      textColor: '#FFFFFF'
                  }
        }
    });

    const { setValue, handleSubmit, clearErrors, reset } = methods;

    const isUnique = (newName) => {
        const exists = savedTags.find(
            (previous) =>
                previous.name === newName && previous.tagId !== tag?.tagId
        );

        return Boolean(!exists);
    };

    const handleTagSelection = (selectedTag) => {
        setValue('selectedColor', selectedTag);
    };

    const handleCloseDrawer = () => {
        dispatch(closeDrawer('createTags'));
        reset();
        clearErrors();
    };

    const submitForm = async (data) => {
        setAddTagLoading(true);

        const newTag = {
            name: data.tagName,
            color: data.selectedColor.textColor,
            backgroundColor: data.selectedColor.backgroundColor
        };

        try {
            if (edit && tag?.tagId) {
                await updateTag({
                    ...newTag,
                    tagId: tag.tagId
                });
            } else {
                const createdNewTag = await createTag(newTag);

                if (openedFromDrawer) {
                    if (openedFromDrawer === 'client') {
                        dispatch(
                            updateDrawer({
                                id: 'client',
                                title,
                                updating: edit,
                                client: item,
                                currentValues,
                                updatedTags: [...currentTags, createdNewTag]
                            })
                        );
                    }
                    if (openedFromDrawer === 'product') {
                        dispatch(
                            updateDrawer({
                                id: 'product',
                                edit,
                                product: item,
                                currentValues,
                                updatedTags: [...currentTags, createdNewTag]
                            })
                        );
                    }
                    if (openedFromDrawer === 'service') {
                        dispatch(
                            updateDrawer({
                                id: 'service',
                                edit,
                                service: item,
                                currentValues,
                                updatedTags: [...currentTags, createdNewTag]
                            })
                        );
                    }
                    if (openedFromDrawer === 'tax') {
                        dispatch(
                            updateDrawer({
                                id: 'tax',
                                edit,
                                tax: item,
                                currentValues,
                                updatedTags: [...currentTags, createdNewTag]
                            })
                        );
                    }
                    if (openedFromDrawer === 'discount') {
                        dispatch(
                            updateDrawer({
                                id: 'discount',
                                edit,
                                discount: item,
                                currentValues,
                                updatedTags: [...currentTags, createdNewTag]
                            })
                        );
                    }
                    if (openedFromDrawer === 'tags') {
                        dispatch(
                            updateDrawer({
                                id: 'tags',
                                type,
                                item,
                                makeDocument,
                                updatedTags: [...currentTags, createdNewTag]
                            })
                        );
                    }
                }
            }

            if (location.pathname === '/items/tags') {
                getPaginatedTags(currentUrlParams);
            }

            triggerToast({
                message: edit
                    ? `Tag "${data.tagName}" updated`
                    : `Tag "${data.tagName}" saved`,
                action: 'saved',
                icon: <TagsIconBlue className={classes.tagsToastIcon} />
            });

            handleCloseDrawer();
        } catch (err) {
            return;
        } finally {
            setAddTagLoading(false);
            handleCloseDrawer();
        }
    };

    return (
        <UncontrolledGenericDrawer
            open={drawerOpen}
            close={handleCloseDrawer}
            id="createTags"
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(submitForm)}
                    className={classes.createTagDrawerContainer}
                >
                    <div>
                        <DrawerHeading
                            startAdornment={<TagsIconBlue />}
                            title={
                                edit && !openedFromDrawer
                                    ? 'Edit Tag'
                                    : 'Add Tag'
                            }
                        />

                        <DrawerBody className={classes.createTagDrawerBody}>
                            <Grid item xs>
                                <TextField
                                    fullWidth
                                    name="tagName"
                                    label="Tag Name"
                                    rules={{
                                        required: 'A tag name is required',
                                        validate: {
                                            unique: (value) =>
                                                isUnique(value) ||
                                                'Tag names must be unique'
                                        },
                                        maxLength: {
                                            value: 50,
                                            message:
                                                'Tag name must be less than 50 characters'
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs>
                                <ColorSwatch
                                    selectionCallback={handleTagSelection}
                                    type="tags"
                                    name="selectedColor"
                                    currentColor={tag?.backgroundColor}
                                />
                            </Grid>
                        </DrawerBody>
                    </div>

                    <DrawerFooter
                        submitButtonId="create-tag-drawer-submit"
                        acceptOptions={{
                            label: 'Save',
                            type: 'submit'
                        }}
                        cancelOptions={{
                            label: 'Cancel',
                            action: handleCloseDrawer
                        }}
                        saving={addTagLoading}
                        disabled={addTagLoading}
                    />
                </form>
            </FormProvider>
        </UncontrolledGenericDrawer>
    );
};

export default CreateTagsDrawer;
